import { ICoreContext, IRequestContext } from "@msdyn365-commerce/core";
import { AttributeValue, Customer, CustomerAttribute } from "@msdyn365-commerce/retail-proxy";
import { IProductAgreement, IProductExtended } from "./types";
import { IAppSettings } from "../settings/app.settings.autogenerated";

function getAppConfig(context: ICoreContext | IRequestContext): IAppSettings | undefined {
    const request = 'request' in context ? context.request : context;
    return request.app.config as IAppSettings | undefined;
}

function getAgreementAttributeRecordIds(appConfig: IAppSettings | undefined): number[] | undefined {
    const recordIds = appConfig?.agreementProductAttributeRecordId
        ?.split(',')
        .map(Number)
        .filter(Boolean);

    return recordIds;
}

type AttributeValueOrCustomerAttribute = AttributeValue | CustomerAttribute;

function getProductAgreement(attributeRecordId: string | undefined, from: 'customer' | 'product', attributes: AttributeValueOrCustomerAttribute[] | undefined): IProductAgreement | undefined {
    const recordId = Number(attributeRecordId);
    if (attributes === undefined || !Number.isFinite(recordId)) {
        return undefined;
    }

    const attribute = attributes.find(attr => attr.RecordId === recordId);
    if (!attribute) {
        return undefined;
    }

    if (from === 'customer' && 'AttributeValue' in attribute && attribute.AttributeValue?.IntegerValue) {
        return {
            AttributeRecordId: attribute.RecordId!,
            Name: attribute.Name!
        };
    }

    // from === 'product'
    if ('IntegerValue' in attribute && typeof attribute.IntegerValue === 'number' && attribute.IntegerValue >= 2) {
        return {
            AttributeRecordId: attribute.RecordId!,
            Name: attribute.Name!
        };
    }

    return undefined;
}

export function getAgreementProductRefinersToHide(context: ICoreContext, customer: Customer | undefined): number[] | undefined {
    const appConfig = getAppConfig(context);
    const refinerIds = getAgreementAttributeRecordIds(getAppConfig(context));

    // Hide all refiners if customer is not available
    if (!customer) {
        return refinerIds;
    }

    const refinersToHide = refinerIds?.filter(refinerId => {
        const attribute = customer.Attributes?.find(x => x.RecordId === refinerId);
        return !attribute?.AttributeValue?.BooleanValue && !attribute?.AttributeValue?.IntegerValue;
    }) || [];

    // Hide HKjottavtale refiner if customer does not have the agreement
    const hkjottavtaleAgreement = getProductAgreement(appConfig?.hkjoottavtaleAttributeRecordId, 'customer', customer.Attributes);
    if (!hkjottavtaleAgreement && Number.isFinite(Number(appConfig?.hkjoottavtaleAttributeRecordId))) {
        refinersToHide.push(Number(appConfig?.hkjoottavtaleAttributeRecordId));
    }

    // Hide Forsvaret refiner if customer does not have the agreement
    const forsvaretAgreement = getProductAgreement(appConfig?.forsvaretAttributeRecordId, 'customer', customer.Attributes);
    if (!forsvaretAgreement && Number.isFinite(Number(appConfig?.forsvaretAttributeRecordId))) {
        refinersToHide.push(Number(appConfig?.forsvaretAttributeRecordId));
    }

    return refinersToHide;
}

export function getProductAgreements(context: ICoreContext | IRequestContext, attributes: AttributeValue[] | undefined): IProductAgreement[] | undefined {
    const appConfig = getAppConfig(context);
    const checkAgreements = getAgreementAttributeRecordIds(appConfig);

    if (!attributes || !checkAgreements) {
        return undefined;
    }

    const agreements: IProductAgreement[] = checkAgreements
        .map(id => attributes.find(attr => attr.RecordId === id))
        .filter(x => x && (x.BooleanValue || x.IntegerValue))
        .map(x => ({
            AttributeRecordId: x?.RecordId,
            Name: x?.Name
        }) as IProductAgreement);

    const hkjoottavtaleAttr = getProductAgreement(appConfig?.hkjoottavtaleAttributeRecordId, 'product', attributes);
    if (hkjoottavtaleAttr) {
        agreements.push(hkjoottavtaleAttr);
    }

    const forsvaretAgreement = getProductAgreement(appConfig?.forsvaretAttributeRecordId, 'customer', attributes);
    if (forsvaretAgreement) {
        agreements.push(forsvaretAgreement);
    }

    appConfig?.companyChainMatches?.forEach(match => {
        const attrId = Number(match.productAttributeRecordId);
        if (Number.isFinite(attrId)) {
            const attribute = attributes.find(x => x.RecordId === attrId);

            if (attribute?.BooleanValue || attribute?.IntegerValue) {
                agreements.push({
                    AttributeRecordId: attrId,
                    Name: match.name || `Company Chain Match - ${match.matchValue}`,
                    Badge: match.badge,
                    TooltipText: match.tooltipText
                });
            }
        }
    });

    return agreements;
}

export function getCustomerAgreements(context: ICoreContext | IRequestContext, customer: Customer | undefined): IProductAgreement[] | undefined {
    const appConfig = getAppConfig(context);
    const checkAgreements = getAgreementAttributeRecordIds(appConfig);
    const attributes = customer?.Attributes;

    if (!attributes || !checkAgreements) {
        return undefined;
    }

    // Find the attributes that match the configured agreement IDs and should be checked for truth
    const findAttributes = checkAgreements
        .map(id => attributes.find(attr => attr.RecordId === id))
        .filter(Boolean) as CustomerAttribute[];

    const agreements: IProductAgreement[] = findAttributes
        // Include only attributes that are Boolean true or Integer with a value greater than 0
        .filter(x => x.AttributeValue?.BooleanValue || x.AttributeValue?.IntegerValue)
        .map(x => ({
            AttributeRecordId: x?.RecordId,
            Name: x?.Name
        }) as IProductAgreement
    );

    const hkjoottavtaleAttr = getProductAgreement(appConfig?.hkjoottavtaleAttributeRecordId, 'customer', attributes);
    if (hkjoottavtaleAttr) {
        agreements.push(hkjoottavtaleAttr);
    }

    const forsvaretAgreement = getProductAgreement(appConfig?.forsvaretAttributeRecordId, 'customer', attributes);
    if (forsvaretAgreement) {
        agreements.push(forsvaretAgreement);
    }

    appConfig?.companyChainMatches?.forEach(match => {
        const attrId = Number(match.productAttributeRecordId);
        const companyChain = customer?.ExtensionProperties?.find(prop => prop.Key === 'COMPANYCHAINID')?.Value?.StringValue;

        if (Number.isFinite(attrId) && companyChain) {
            let matcher: (value: string) => boolean;
            switch (match.matchType) {
                case 'exact':
                    matcher = value => value === match.matchValue;
                    break;
                case 'startsWith':
                    matcher = value => value.startsWith(match.matchValue);
                    break;
                case 'endsWith':
                    matcher = value => value.endsWith(match.matchValue);
                    break;
                case 'contains':
                    matcher = value => value.includes(match.matchValue);
                    break;
                default:
                    matcher = () => false;
            }

            if (matcher(companyChain)) {
                agreements.push({
                    AttributeRecordId: attrId,
                    Name: match.name || `Company Chain Match - ${match.matchValue}`,
                    Badge: match.badge,
                    TooltipText: match.tooltipText
                });
            }
        }
    });

    return agreements;
}

export function hasAgreementForProduct(product: IProductExtended, customerAgreements: IProductAgreement[] | undefined): boolean {
    if (!product.Agreements || product.Agreements.length === 0 || !customerAgreements || customerAgreements.length === 0) {
        return false;
    }

    return product.Agreements?.some(productAgreement =>
        customerAgreements.some(customerAgreement => customerAgreement.AttributeRecordId === productAgreement.AttributeRecordId)
    ) || false;
}

export function getAgreementBadgeForProduct(product: IProductExtended | undefined): IProductAgreement | undefined {
    return product?.Agreements?.find(agreement => agreement.Badge);
}
